import * as React from 'react'
import { FaIcon, faQuestionCircleRegular, faChartPieSimpleSolid } from '@/lib/fa'
import { getActivityIcon } from '../../../../helpers/ActivityIconResolver'
import { getActivityName } from '../../../../helpers/ActivityNameResolver'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import {
  formatDateTime,
  getDurationBetweeenDates,
} from '@/app/common/dateHelper'
import ActivityListRowItem from './ActivityListRowItem'
import { IconProp } from '@fortawesome/fontawesome-svg-core'

const ActivityListRow = React.forwardRef<
  HTMLTableRowElement,
  {
    activity: any
    onMouseEnter: any
    onMouseLeave: any
    isHovered: any
    isSelected?: boolean
    onClick?: (e: React.MouseEvent) => void
    onContextMenu?: (e: React.MouseEvent) => void
    onShowChart?: () => void
  }
>(
  (
    {
      activity,
      onMouseEnter,
      onMouseLeave,
      isHovered,
      isSelected,
      onClick,
      onContextMenu,
      onShowChart,
    },
    ref,
  ) => {
    const { t } = useLocalize()
    const yes = t('answer.yes')
    const no = t('answer.no')
    const [showCrewTooltip, setShowCrewTooltip] = React.useState(false)
    // Vehicle-sourced activities (recorded by the vehicle, missing from the driver card)
    // are shown grayed out and read-only.
    const isVehicleSource = activity?.isVehicleSource === true

    return (
      <tr
        ref={ref}
        className={`notches-border--right tw-w-fit tw-border-b tw-transition-colors ${
          isVehicleSource
            ? 'tw-bg-gray-100 tw-opacity-60'
            : `tw-cursor-pointer ${
                isSelected
                  ? 'tw-z-10 tw-bg-primary/30 tw-ring-1 '
                  : isHovered
                    ? 'tw-z-10 tw-bg-primary/10 tw-shadow-lg tw-ring-2 tw-ring-primary/50'
                    : 'tw-bg-white hover:tw-bg-primary/10'
              }`
        }`}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={isVehicleSource ? undefined : onClick}
        onContextMenu={isVehicleSource ? undefined : onContextMenu}
      >
        <ActivityListRowItem
          size="small"
          bolder={false}
          icon={
            <FaIcon
              icon={getActivityIcon(activity?.type) as IconProp}
              className="tw-h-3 tw-w-3 tw-text-content-base"
            />
          }
        >
          <span className="tw-inline-flex tw-items-center tw-w-full tw-justify-between tw-gap-1.5">
            {t(getActivityName(activity.type))}
            {onShowChart && (
              <button
                type="button"
                title={t('driverModule.show-chart')}
                onClick={(e) => {
                  e.stopPropagation()
                  onShowChart()
                }}
                className="tw-inline-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded tw-text-primary hover:tw-bg-primary/10"
              >
                <FaIcon icon={faChartPieSimpleSolid} className="tw-h-3.5 tw-w-3.5" />
              </button>
            )}
          </span>
        </ActivityListRowItem>
        <ActivityListRowItem size="small" bolder={false}>
          {formatDateTime(activity.startTime)}
        </ActivityListRowItem>
        <ActivityListRowItem size="small" bolder={false}>
          {formatDateTime(activity.endTime)}
        </ActivityListRowItem>
        <ActivityListRowItem size="small" bolder={false}>
          {activity.endTime
            ? getDurationBetweeenDates(activity.startTime, activity.endTime)
            : '-'}
        </ActivityListRowItem>
        <ActivityListRowItem bolder={false} size="small">
          {activity?.registrationNumber || '-'}
        </ActivityListRowItem>
        <ActivityListRowItem bolder={false} size="small">
          <div className="tw-flex tw-items-center tw-gap-1.5">
            <span>{activity?.isCrew === true ? yes : no}</span>
            {activity?.isCrew === true && activity?.coDriver && (
              <div className="tw-relative">
                <FaIcon
                  icon={faQuestionCircleRegular}
                  className="tw-h-3.5 tw-w-3.5 tw-cursor-help tw-text-gray-400 hover:tw-text-gray-600"
                  onMouseEnter={() => setShowCrewTooltip(true)}
                  onMouseLeave={() => setShowCrewTooltip(false)}
                />
                {showCrewTooltip && (
                  <div className="tw-absolute tw-bottom-full tw-left-1/2 tw-z-50 tw-mb-2 tw-w-max tw-max-w-xs -tw-translate-x-1/2 tw-rounded-md tw-bg-gray-800 tw-px-3 tw-py-2 tw-text-xs tw-text-white tw-shadow-lg">
                    <div className="tw-mb-1 tw-font-semibold">
                      {t('driverModule.crew-members')}:
                    </div>
                    <div className="tw-space-y-0.5">
                      {activity.coDriver
                        .split(',')
                        .map((name: string, idx: number) => (
                          <div key={idx} className="tw-text-gray-200">
                            • {name.trim()}
                          </div>
                        ))}
                    </div>
                    <div className="tw-absolute tw-left-1/2 tw-top-full -tw-translate-x-1/2 tw-border-4 tw-border-transparent tw-border-t-gray-800" />
                  </div>
                )}
              </div>
            )}
          </div>
        </ActivityListRowItem>
        <ActivityListRowItem bolder={false} size="small">
          {activity?.isFerryCrossing === true ? yes : no}
        </ActivityListRowItem>
        <ActivityListRowItem bolder={false} size="small">
          {activity?.isOutOfScope === true ? yes : no}
        </ActivityListRowItem>
        <ActivityListRowItem bolder={false} size="small">
          {activity?.isManualEntry === true ? yes : no}
        </ActivityListRowItem>
      </tr>
    )
  },
)

ActivityListRow.displayName = 'ActivityListRow'

export default ActivityListRow
